import { Button, CircularProgress, FormControl, Input, Option, Select, SvgIcon, Table, Typography } from "@mui/joy";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import React from "react";
import { Util } from "../Helpers/Util";

import { debounce } from "lodash";
import { useDebounce } from "usehooks-ts";
import TicketDialog from "../Dialogs/TicketDialog";
import TicketUploadDialog, { UploadTicket } from "../Dialogs/TicketUploadDialog";
import { Mechanic } from "../Interfaces/Mechanic";
import { Elevator } from "../Interfaces/Elevator";

interface TicketListingProps {
	buildingId?: string;
	ticketTypesId?: string;
	recommendations?: boolean;
	outOfService?: boolean;
	sales?: boolean;
}

export default function TicketListing(props: TicketListingProps) {
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(10);
	const [dateFilter, setDateFilter] = React.useState(null as any);
	const [ticketTypeFilter, setTicketTypeFilter] = React.useState(props.ticketTypesId ? [props.ticketTypesId] : Util.ticketTypes.map((x) => x.ticket_type_id));
	const [globalFilter, setGlobalFilter] = React.useState("" as string);
	const globalFilterD = useDebounce<string>(globalFilter, 300);
	const [orderBy, setOrderBy] = React.useState(2);
	const [order, setOrder] = React.useState(true);
	const [tickets, setTickets] = React.useState([] as Array<any>);
	const [loading, setLoading] = React.useState(false);
	const [dialogId, setDialogId] = React.useState<string>();
	const [showDialog, setShowDialog] = React.useState(false);
	const [uploadDialog, setUploadDialog] = React.useState(false);
	const [mechanics, setMechanics] = React.useState<Array<Mechanic>>([]);
	const [elevators, setElevators] = React.useState<Array<Elevator>>([]);
	const [uploadTicket, setUploadTicket] = React.useState<UploadTicket>({
		ticketDate: new Date(),
		ticketTypesIds: [],
	} as any as UploadTicket);

	React.useEffect(() => {
		getTickets();
		!elevators.length && getElevators();
		!mechanics.length && getMechanics();
	}, [pageNumber, pageSize, globalFilterD, ticketTypeFilter, order, orderBy, props.buildingId]);

	React.useEffect(() => {
		setPageNumber(0);
	}, [props.buildingId]);

	const getElevators = () => {
		fetch("Building/GetAllElevators", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setElevators(x));
	};

	const getMechanics = () => {
		fetch("User/GetMechanics", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({}),
		})
			.then((x) => x.json())
			.then((x) => setMechanics(x));
	};

	const markSaleComplete = (ticketId: string) => {
		fetch("Ticket/CompleteSale", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ ticketsId: ticketId }),
		}).then(() => getTickets());
	};

	const getTickets = debounce(() => {
		setLoading(true);
		const ob = ["building_name", "serial_number", "ticket_number", "date_entered", "ticket_types", "is_submitted"];
		if (props.buildingId) {
			ob.splice(0, 1);
		}
		const orderFilter = [{ ColumnName: ob[orderBy], IsDescending: order }];

		let dt = Util.Formatter(dateFilter, "D");
		if (dt === "01/01/1900" || dt === "Invalid date") dt = null;

		fetch("/Ticket/GetPagedTickets", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				buildingId: props.buildingId || null,
				ticketTypeId: ticketTypeFilter,
				recommendations: props.recommendations || false,
				pageNumber: pageNumber,
				pageSize: pageSize,
				orderBy: orderFilter,
				ticketDate: dt,
				globalFilter: globalFilter,
				outOfService: props.outOfService || false,
				sales: props.sales || false,
			}),
		})
			.then((x) => x.json())
			.then((y) => {
				setTickets(y);
				setLoading(false);
			});
	}, 150);

	const rowCounts = [5, 10, 25, 50];
	const maxPages = tickets.length > 0 ? Math.ceil(tickets[0]["total_row_count"] / pageSize) : 1;
	const cols = ["Building Name", "Serial Number", "Ticket Number", "Date", "Type", "Submitted?"];
	if (props.buildingId) {
		cols.splice(0, 1);
	}

	if (props.sales) {
		cols.push("Mark Complete");
	}
	return (
		<>
			<TicketUploadDialog
				open={uploadDialog}
				close={() => {
					setUploadDialog(false);
					getTickets();
				}}
				buildingId={(props.buildingId as string) || (uploadTicket?.buildingId as string)}
				mechanics={mechanics}
				elevators={elevators}
				ticket={uploadTicket}
			/>
			{showDialog && <TicketDialog ticketsId={dialogId as string} close={() => setShowDialog(false)} />}
			<div>
				{!props.buildingId && (
					<Typography sx={{ display: "inline-block" }} level="title-lg">
						Tickets
					</Typography>
				)}
				<div style={{ float: "right", display: "flex" }}>
					{Util.CurrentRole === "Admin" && props.buildingId && !props.ticketTypesId && !props.recommendations && !props.outOfService && (
						<Button
							sx={{ mr: 1, mt: 1 }}
							onClick={() => {
								setUploadDialog(true);
								setUploadTicket({
									ticketDate: new Date(),
									ticketTypesIds: [],
								} as any as UploadTicket);
							}}>
							Add Paper Ticket
						</Button>
					)}
					<FormControl sx={{ marginRight: 1, mt: 1 }}>
						<Input
							name="Search"
							placeholder="Global Search"
							onChange={(event) => {
								setGlobalFilter(event.target.value);
							}}
							value={globalFilter}
						/>
					</FormControl>
					{Util.isNullOrEmpty(props.ticketTypesId) && (
						<Select
							placeholder="Ticket Types"
							value={ticketTypeFilter}
							renderValue={() => "Ticket Types"}
							multiple
							sx={{ mt: 1 }}
							onChange={(event, value) => {
								setTicketTypeFilter(value ?? []);
							}}>
							<>
								{Util.ticketTypes.map((x) => (
									<Option value={x.ticket_type_id}>{x.description}</Option>
								))}
							</>
						</Select>
					)}
				</div>
			</div>
			<Table stripe="even" hoverRow stickyHeader sx={{ overflowY: "auto" }}>
				<thead>
					<tr>
						{cols.map((x, idx) => (
							<th
								style={{ cursor: "pointer" }}
								onClick={() => {
									setOrderBy(idx);
									setOrder(orderBy === idx ? !order : true);
								}}>
								{x}
								{orderBy === idx ? <SvgIcon onClick={() => setOrder(!order)} component={order ? ArrowDownwardIcon : ArrowUpwardIcon} sx={{ cursor: "pointer", position: "absolute" }} /> : <></>}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{loading && (
						<tr>
							<td colSpan={6}>
								<div style={{ display: "flex", flexDirection: "column", width: "100%", alignItems: "center" }}>
									<CircularProgress />
								</div>
							</td>
						</tr>
					)}
					{!loading &&
						tickets.map((t) => {
							return (
								<tr
									onClick={() => {
										if (!t.is_pdf_data) {
											setShowDialog(true);
											setDialogId(t.tickets_id);
										} else {
											fetch("Ticket/GetUploadTicketData", {
												method: "Post",
												cache: "no-cache",
												headers: {
													"Content-Type": "application/json",
												},
												body: JSON.stringify(t.tickets_id),
											})
												.then((x) => x.json())
												.then((x) => {
													setUploadDialog(true);
													setUploadTicket({
														elevatorId: x.elevators_id,
														buildingId: x.buildings_id,
														ticketDate: Util.ParseDate(x.date_entered),
														submitted: x.submitted,
														ticketTypesIds: (x.ticket_type ?? "").split(","),
														ticketsId: x.tickets_id,
														mechanicId: x.user_id,
														ticketNumber: x.ticket_number,
														workPerformed: x.work_performed,
														buildingName: x.building_name,
													} as UploadTicket);
												});
										}
									}}>
									{!props.buildingId && <td>{t.building_name}</td>}
									<td>{t.serial_number}</td>
									<td>{t.ticket_number}</td>
									<td>{Util.Formatter(t.date_entered, "D")}</td>
									<td>{t.ticket_types}</td>
									<td>{Util.Formatter(t.is_submitted, "L")}</td>
									{props.sales && (
										<td>
											<Button
												onClick={(event) => {
													markSaleComplete(t.tickets_id);
													event?.preventDefault();
													event?.stopPropagation();
												}}>
												Mark Sale Complete
											</Button>
										</td>
									)}
								</tr>
							);
						})}
				</tbody>
				{!loading && (
					<tfoot>
						<tr>
							<td colSpan={cols.length}>
								<div style={{ float: "right" }}>
									<span>Rows per Page:</span>
									<div style={{ display: "inline-block", marginLeft: "4px", marginRight: "4px" }}>
										<Select
											size="sm"
											value={pageSize}
											onChange={(x, y) => {
												setPageSize(y as number);
											}}>
											{rowCounts.map((x) => (
												<Option value={x}>{x}</Option>
											))}
										</Select>
									</div>
									<span>
										<KeyboardArrowLeftIcon onClick={() => pageNumber > 0 && setPageNumber(pageNumber - 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} /> {`Page ${pageNumber + 1} of ${maxPages}`} <KeyboardArrowRightIcon onClick={() => maxPages > pageNumber + 1 && setPageNumber(pageNumber + 1)} sx={{ verticalAlign: "middle", cursor: "pointer" }} />
									</span>
								</div>
							</td>
						</tr>
					</tfoot>
				)}
			</Table>
		</>
	);
}
